import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { IconComponent } from '../icon/icon.component';
import type { UiIconName } from '../icon/icon.component';
import { SpinnerComponent } from '../spinner/spinner.component';

export type UiButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type UiButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [IconComponent, SpinnerComponent],
  template: `
    <button
      [attr.type]="type()"
      class="ui-btn"
      [disabled]="disabled() || loading()"
      [class]="hostClass()"
      [attr.aria-busy]="loading() ? 'true' : null"
      (click)="clicked.emit($event)"
    >
      @if (loading()) {
        <span class="ui-btn__spinner" aria-hidden="true">
          <app-spinner size="sm" [label]="'Loading'" />
        </span>
      } @else if (iconName()) {
        <span class="ui-btn__icon" aria-hidden="true">
          <app-icon [name]="iconName()!" [size]="iconSizePx()" />
        </span>
      }
      <span class="ui-btn__label">
        <ng-content />
      </span>
    </button>
  `,
  styles: `
    :host {
      display: inline-block;
    }
    :host(.ui-btn-host--block) {
      display: block;
      width: 100%;
    }
    .ui-btn {
      font-family: var(--ui-font-sans);
      font-weight: var(--ui-font-weight-medium);
      border-radius: var(--ui-radius-control);
      border: 1px solid transparent;
      box-sizing: border-box;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--ui-space-2);
      transition:
        background-color var(--ui-duration-fast) var(--ui-ease),
        border-color var(--ui-duration-fast) var(--ui-ease),
        color var(--ui-duration-fast) var(--ui-ease);
    }
    .ui-btn:focus-visible {
      outline: none;
      box-shadow: none;
    }
    .ui-btn:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .ui-btn--sm {
      font-size: var(--ui-button-font-size);
      padding: 0 var(--ui-space-3);
      min-height: var(--ui-control-height);
      max-height: var(--ui-control-height);
    }
    .ui-btn--md {
      font-size: var(--ui-button-font-size);
      padding: 0 var(--ui-space-4);
      min-height: var(--ui-control-height);
      max-height: var(--ui-control-height);
    }
    .ui-btn--lg {
      font-size: var(--ui-button-font-size);
      padding: 0 var(--ui-space-4);
      min-height: var(--ui-control-height);
      max-height: var(--ui-control-height);
    }
    .ui-btn--primary {
      background: var(--ui-color-primary);
      color: var(--ui-color-primary-contrast);
    }
    .ui-btn--primary:hover:not(:disabled) {
      filter: brightness(0.95);
    }
    .ui-btn--secondary {
      background: var(--ui-color-primary-subtle);
      color: var(--ui-color-primary);
    }
    .ui-btn--secondary:hover:not(:disabled) {
      filter: brightness(0.97);
    }
    .ui-btn--outline {
      background: var(--ui-color-surface);
      border-color: var(--ui-color-border);
      color: var(--ui-color-text);
    }
    .ui-btn--ghost {
      background: transparent;
      color: var(--ui-color-primary);
    }
    .ui-btn--danger {
      background: var(--ui-color-danger);
      color: var(--ui-color-danger-contrast);
    }
    .ui-btn--block {
      width: 100%;
    }
    .ui-btn--pill {
      border-radius: var(--ui-radius-full);
    }
    .ui-btn__spinner {
      display: inline-flex;
      --ui-spinner-color: currentColor;
    }
    .ui-btn__icon {
      display: inline-flex;
    }
  `,
  host: {
    '[class.ui-btn-host--block]': 'fullWidth()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
  readonly variant = input<UiButtonVariant>('primary');
  readonly size = input<UiButtonSize>('md');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly iconName = input<UiIconName | null>(null);
  readonly fullWidth = input(false);
  /** Fully rounded ends (pill), matching Remmi marketing auth buttons. */
  readonly shape = input<'default' | 'pill'>('default');

  readonly clicked = output<MouseEvent>();

  protected iconSizePx(): number {
    return this.size() === 'sm' ? 12 : this.size() === 'lg' ? 16 : 14;
  }

  readonly hostClass = computed(() => {
    const v = this.variant();
    const s = this.size();
    const block = this.fullWidth() ? ' ui-btn--block' : '';
    const pill = this.shape() === 'pill' ? ' ui-btn--pill' : '';
    return `ui-btn--${v} ui-btn--${s}${block}${pill}`;
  });
}
